import { CheckCircle2, LucideIcon, XCircle } from "lucide-react";

interface TProps {
    heading?: string,
    subHeading?: string,
    titles?: string[],
    comparisons?: {
        feature: string,
        icon: LucideIcon,
        greet: string,
        competitor: string
    }[];
}

const ComparisonSection = ({
    heading = "How Greet Stands Out",
    subHeading = "A quick comparison of Greet vs traditional ticketing platforms.",
    comparisons,
    titles = [
        "Feature",
        "Greet",
        "Competitors",
    ], }: TProps) => {

    return (
        <section className='relative max-w-full bg-[#0B0D17] px-4 py-16 sm:px-6 xl:py-[100px] overflow-hidden'>
            <div className='relative z-10 flex flex-col text-center'>
                <h2 className='text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl leading-11 sm:leading-12 md:leading-16'>
                    {heading}
                </h2>
                <p className='mt-2 text-base font-semibold text-gray-500'>
                    {subHeading}
                </p>
            </div>

            <div className="max-w-6xl mx-auto mt-8 bg-[#13151C] p-8 rounded-2xl">
                <div className="hidden md:grid grid-cols-12 gap-6 mb-8 text-gray-400 font-medium text-sm tracking-wide">
                    {titles?.map((title: string, index: number) => <p className="col-span-4 text-gray-300 text-base" key={index}>{title}</p>)}
                </div>

                <div className="flex flex-col gap-4 [&>div:not(:first-child)]:border-t [&>div:not(:first-child)]:border-gray-700/30 md:border-t md:border-gray-700/30">
                    {comparisons.map((item, index) => {
                        const Icon = item.icon;
                        return (
                            <div
                                key={index}
                                className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-center md:items-stretch pt-4"
                            >
                                <div className="col-span-1 md:col-span-4 flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                                        <Icon className="w-5 h-5 text-primary" />
                                    </div>
                                    <span className="text-white font-medium text-lg">{item.feature}</span>
                                </div>

                                <div className="col-span-1 md:col-span-4 cursor-pointer group/greet">
                                    <span className="md:hidden text-xs text-gray-500 mb-2 block transition-all duration-300 group-hover/greet:-translate-y-2">Greet</span>
                                    <div className="relative overflow-hidden bg-[#052e16]/40 border border-green-900/30 rounded-xl p-4 flex items-start gap-3 h-full min-h-[80px] md:min-h-0 transition-all duration-300 group-hover/greet:bg-[#052e16]/60 group-hover/greet:border-green-500/50 group-hover/greet:shadow-[0_0_30px_-5px_rgba(34,197,94,0.3)] group-hover/greet:-translate-y-2">
                                        <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5 md:mt-0" />
                                        <span className="text-gray-200 text-sm font-medium leading-relaxed">
                                            {item.greet}
                                        </span>

                                        <div className="absolute bottom-0 left-6 right-6 h-[3px] bg-green-500 rounded-t-full opacity-0 transform scale-x-0 translate-y-2 transition-all duration-500 ease-out group-hover/greet:opacity-100 group-hover/greet:scale-x-100 group-hover/greet:translate-y-0 shadow-[0_-2px_10px_rgba(34,197,94,0.8)]" />
                                    </div>
                                </div>

                                <div className="col-span-1 md:col-span-4 cursor-pointer group/comp">
                                    <span className="md:hidden text-xs text-gray-500 mb-2 block transition-all duration-300 group-hover/comp:-translate-y-2">Competitors</span>
                                    <div className="bg-[#2b1215]/40 border border-red-900/30 rounded-xl p-4 flex items-start gap-3 h-full min-h-[80px] md:min-h-0 transition-all duration-300 group-hover/comp:-translate-y-2">
                                        <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5 md:mt-0" />
                                        <span className="text-gray-400 text-sm font-medium leading-relaxed group-hover/comp:text-primary">
                                            {item.competitor}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

export default ComparisonSection;